import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiArrowLeft, HiChevronDown, HiPencil, HiTrash, HiSearch, HiChevronLeft, HiChevronRight, HiUpload } from 'react-icons/hi';
import {
  fetchChapterList,
  createChapter,
  updateChapter,
  deleteChapter,
} from '../../services/chapterService';
import { uploadQuizCsv } from '../../services/quizService';
import { uploadImage } from '../../services/assetService';
import UploadCsvIcon from '../../assets/icons/upload-csv.svg';
import Toast from '../../components/toaster';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { chaptersCache } from './chaptersCache';
import { invalidateLessonCache } from '../lesson/lessonCache';
import { invalidateLessonContentCache } from '../lessonContent/lessonContentCache';
import { bumpModuleChaptersCount } from '../module/moduleCache';

const getChapterChildCount = (chapter) => {
  if (!chapter || typeof chapter !== 'object') return null;

  const fromArray = (arr) => (Array.isArray(arr) ? arr.length : null);
  const fromNumber = (n) => (Number.isFinite(Number(n)) ? Number(n) : null);

  return (
    fromArray(chapter.units) ??
    fromArray(chapter.lessons) ??
    fromArray(chapter.children) ??
    fromNumber(chapter.units_count) ??
    fromNumber(chapter.unit_count) ??
    fromNumber(chapter.total_units) ??
    fromNumber(chapter.totalUnits) ??
    fromNumber(chapter.children_count) ??
    fromNumber(chapter.child_count) ??
    null
  );
};

const parseEarlyAccessDays = (value) => {
  if (value === '' || value === null || value === undefined) return 0;

  const stringValue = String(value).trim();
  if (!/^\d+$/.test(stringValue)) return NaN;

  const parsed = Number(stringValue);
  return Number.isInteger(parsed) ? parsed : NaN;
};

const getChapterEarlyAccessDays = (chapter) => {
  if (!chapter || typeof chapter !== 'object') return 0;

  const candidate = chapter.early_access_days ?? chapter.earlyAccessDays;
  const parsed = parseEarlyAccessDays(candidate);

  if (Number.isNaN(parsed) || parsed < 0) return 0;
  return parsed;
};

const getChapterDisplayEarlyAccessDays = (chapter) => {
  const configuredDays = getChapterEarlyAccessDays(chapter);
  if (configuredDays <= 0) return 0;

  const createdAtValue = chapter?.created_at ?? chapter?.createdAt;
  if (!createdAtValue) return configuredDays;

  const createdAt = new Date(createdAtValue);
  if (Number.isNaN(createdAt.getTime())) return configuredDays;

  const now = new Date();
  const localCreatedDate = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
  const localCurrentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const msPerDay = 24 * 60 * 60 * 1000;
  const elapsedDays = Math.floor((localCurrentDate.getTime() - localCreatedDate.getTime()) / msPerDay);

  if (!Number.isFinite(elapsedDays) || elapsedDays <= 0) return configuredDays;

  return Math.max(0, configuredDays - elapsedDays);
};

const Chapters = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromName = location.state?.fromName;
  const moduleName = location.state?.moduleName || fromName;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [chapters, setChapters] = useState(chaptersCache.moduleId === moduleId ? (chaptersCache.chapters || []) : []);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [editingUploadingImage, setEditingUploadingImage] = useState(false);
  const [editingUploadError, setEditingUploadError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderChapters, setReorderChapters] = useState([]);
  const [reorderSaveLoading, setReorderSaveLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [uploadResult, setUploadResult] = useState({
    successful: [],
    skipped: [],
    error: [],
  });
  const [paginationLoading, setPaginationLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(16);
  const [addChapterErrors, setAddChapterErrors] = useState({
    title: '',
    description: '',
    order: '',
    early_access_days: '',
  });
  const [newChapter, setNewChapter] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    order: 1,
    early_access_days: '0',
    image: null,
    imagePreview: null,
  });
  const [editingChapter, setEditingChapter] = useState({
    chapter_id: null,
    title: '',
    description: '',
    thumbnail_url: '',
    order: 1,
    early_access_days: '0',
    is_active: true,
    image: null,
    imagePreview: null,
  });

  // Static sample data
  const staticChapters = [
    {
      chapter_id: '1',
      id: '1',
      title: 'Introduction to React',
      description: 'Learn the fundamentals of React including components, props, and state management. This chapter covers the basics you need to get started with React development.',
      order: 1,
      is_active: true,
      thumbnail_url: '',
    },
    {
      chapter_id: '2',
      id: '2',
      title: 'Advanced Hooks',
      description: 'Deep dive into React Hooks including useState, useEffect, useContext, and custom hooks. Master the modern way of building React applications.',
      order: 2,
      is_active: true,
      thumbnail_url: '',
    },
    {
      chapter_id: '3',
      id: '3',
      title: 'State Management',
      description: 'Explore different state management solutions including Context API, Redux, and Zustand. Learn when to use each approach for optimal performance.',
      order: 3,
      is_active: false,
      thumbnail_url: '',
    },
    {
      chapter_id: '4',
      id: '4',
      title: 'Routing and Navigation',
      description: 'Master React Router and learn how to implement client-side routing, protected routes, and navigation patterns in your applications.',
      order: 4,
      is_active: true,
      thumbnail_url: '',
    },
    {
      chapter_id: '5',
      id: '5',
      title: 'API Integration',
      description: 'Learn how to fetch data from APIs, handle loading states, error handling, and implement data caching strategies for better user experience.',
      order: 5,
      is_active: true,
      thumbnail_url: '',
    },
    {
      chapter_id: '6',
      id: '6',
      title: 'Testing React Applications',
      description: 'Comprehensive guide to testing React components using Jest and React Testing Library. Learn best practices for writing maintainable tests.',
      order: 6,
      is_active: false,
      thumbnail_url: '',
    },
  ];

  const staticModule = {
    module_id: '1',
    id: '1',
    title: 'React Fundamentals',
    name: 'React Fundamentals',
    description: 'Complete guide to React development',
  };

  // Guard flags to prevent duplicate API calls
  const isLoadingRef = useRef(false);
  const lastModuleIdRef = useRef(null);

  // Debounce search query
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const searchDebounceTimerRef = useRef(null);
  const paginationDelayTimerRef = useRef(null);
  const paginationInitialSkipRef = useRef(true);

  // Load module details and chapters
  const loadData = async (currentSearch = debouncedSearch) => {
    // Validate moduleId is present
    if (!moduleId) {
      // console.error('Module ID is missing. Cannot load chapters.');
      setChapters([]);
      setIsLoading(false);
      return;
    }

    const isFirstLoad = chaptersCache.firstLoad || chaptersCache.moduleId !== moduleId;
    const isSearchChange = currentSearch !== (chaptersCache.lastSearch || '');

    // Prevent duplicate concurrent calls
    if (isLoadingRef.current) {
      return;
    }

    // Prevent duplicate calls with same moduleId and search
    if (lastModuleIdRef.current === moduleId && !isSearchChange) {
      return;
    }

    if (isFirstLoad || isSearchChange) {
      setIsLoading(true);
    }

    // Mark as loading and track moduleId
    lastModuleIdRef.current = moduleId;
    isLoadingRef.current = true;
    try {
      // Reset state first to prevent showing stale data from previous module
      setChapters([]);

      // Fetch chapters - always pass moduleId to ensure proper filtering
      const chaptersResponse = await fetchChapterList(moduleId, 1, 100, currentSearch, {});

      // Set loading to false immediately after API calls resolve
      setIsLoading(false);

      // Extract chapters array from response
      let allChapters = [];
      if (chaptersResponse.data && Array.isArray(chaptersResponse.data)) {
        allChapters = chaptersResponse.data;
      } else if (Array.isArray(chaptersResponse)) {
        allChapters = chaptersResponse;
      } else {
        allChapters = chaptersResponse.chapters || chaptersResponse.results || [];
      }

      // Client-side filtering: Filter chapters by module_id as a safety measure
      // This ensures that even if the API returns all chapters, we only show chapters for this module
      const filteredChapters = allChapters.filter((chapter) => {
        const chapterModuleId = chapter.module_id || chapter.moduleId || chapter.module?.module_id || chapter.module?.id;
        // Convert both to strings for comparison to handle number/string mismatches
        return String(chapterModuleId) === String(moduleId);
      });

      // console.log(`[Chapters] Loading for moduleId: ${moduleId}`);
      // console.log(`[Chapters] Total chapters from API: ${allChapters.length}`);
      // console.log(`[Chapters] Filtered chapters for module ${moduleId}: ${filteredChapters.length}`);

      setChapters(filteredChapters);

      chaptersCache.chapters = filteredChapters;
      chaptersCache.moduleId = moduleId;
      chaptersCache.lastSearch = currentSearch;
      chaptersCache.firstLoad = false;
    } catch (error) {
      // console.error('Error loading data:', error);
      alert(error.message || 'Failed to load chapters. Please try again.');
      setChapters([]);
      setIsLoading(false);
      chaptersCache.firstLoad = false;
    } finally {
      // Guarantee loader stops even if there's an unexpected error
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Debounce search query
  useEffect(() => {
    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page when search changes
    }, 500); // 500ms debounce

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Full-page loader when changing pages (client-side pagination)
  useEffect(() => {
    if (paginationInitialSkipRef.current) {
      paginationInitialSkipRef.current = false;
      return;
    }
    if (paginationDelayTimerRef.current) {
      clearTimeout(paginationDelayTimerRef.current);
      paginationDelayTimerRef.current = null;
    }
    setPaginationLoading(true);
    paginationDelayTimerRef.current = setTimeout(() => {
      setPaginationLoading(false);
      paginationDelayTimerRef.current = null;
    }, 200);
    return () => {
      if (paginationDelayTimerRef.current) {
        clearTimeout(paginationDelayTimerRef.current);
        paginationDelayTimerRef.current = null;
      }
    };
  }, [page]);

  useEffect(() => {
    // Log moduleId change for debugging
    // console.log(`[Chapters] Module ID changed to: ${moduleId}`);

    const hasCache = chaptersCache.chapters !== null && chaptersCache.moduleId === moduleId && !chaptersCache.firstLoad && debouncedSearch === (chaptersCache.lastSearch || '');

    if (hasCache && moduleId) {
      setChapters(chaptersCache.chapters);
      lastModuleIdRef.current = moduleId;
      // Background revalidate so counts update when navigating back from Unit/LessonContent pages.
      setTimeout(() => {
        loadData(debouncedSearch);
      }, 0);
      return;
    }

    // Reset guard when moduleId changes (allows new calls for different moduleId)
    if (lastModuleIdRef.current !== moduleId) {
      lastModuleIdRef.current = null;
    }

    // Reset all state when moduleId changes to prevent data leakage
    setChapters([]);
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setChapterToDelete(null);
    setOpenDropdownId(null);
    setNewChapter({
      title: '',
      description: '',
      thumbnail_url: '',
      order: 1,
      early_access_days: '0',
      image: null,
      imagePreview: null,
    });
    setEditingChapter({
      chapter_id: null,
      title: '',
      description: '',
      thumbnail_url: '',
      order: 1,
      early_access_days: '0',
      is_active: true,
      image: null,
      imagePreview: null,
    });

    // Load data for the new module
    if (moduleId) {
      loadData(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, debouncedSearch]);

  // Cleanup: Restore scrolling when modal closes
  useEffect(() => {
    if (!showReorderModal) {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [showReorderModal]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.status-dropdown-container')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const handleAddChapter = () => {
    setAddChapterErrors({
      title: '',
      description: '',
      order: '',
      early_access_days: '',
    });
    setUploadError('');
    setUploadingImage(false);
    setShowAddModal(true);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setUploadError('');
      setUploadingImage(true);
      setNewChapter({
        ...newChapter,
        image: file,
        imagePreview: previewUrl,
        thumbnail_url: '',
      });

      try {
        const uploadedUrl = await uploadImage(file);
        setNewChapter(prev => ({ ...prev, imagePreview: uploadedUrl, thumbnail_url: uploadedUrl }));
        setUploadError('');
      } catch (error) {
        console.error('Image upload failed:', error);
        setUploadError(error.message || 'Failed to upload image. Please try again.');
        setNewChapter(prev => ({ ...prev, image: null, imagePreview: null, thumbnail_url: '' }));
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleThumbnailUrlChange = (e) => {
    const url = e.target.value;
    if (newChapter.imagePreview && newChapter.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(newChapter.imagePreview);
    }
    setNewChapter({
      ...newChapter,
      thumbnail_url: url,
      image: null,
      imagePreview: url || null,
    });
  };

  const handleAddChapterSubmit = async () => {
    // Validate moduleId is present
    if (!moduleId) {
      alert('Module ID is missing. Cannot add chapter. Please navigate back to modules and try again.');
      return;
    }

    // Validate fields and set errors
    const errors = {
      title: '',
      description: '',
      order: '',
      early_access_days: '',
    };

    let hasErrors = false;

    if (!newChapter.title.trim()) {
      errors.title = 'Chapter title is required';
      hasErrors = true;
    }

    if (!newChapter.description.trim()) {
      errors.description = 'Description is required';
      hasErrors = true;
    }

    if (newChapter.order && (isNaN(newChapter.order) || parseInt(newChapter.order) < 1)) {
      errors.order = 'Order must be a positive number';
      hasErrors = true;
    }

    const earlyAccessDays = parseEarlyAccessDays(newChapter.early_access_days);
    if (Number.isNaN(earlyAccessDays) || earlyAccessDays < 0) {
      errors.early_access_days = 'Early access days must be an integer greater than or equal to 0';
      hasErrors = true;
    }

    setAddChapterErrors(errors);

    if (hasErrors) {
      return;
    }

    // console.log(`[Chapters] Adding chapter to moduleId: ${moduleId}`);

    setAdding(true);
    try {
      // Use uploaded URL from imagePreview or thumbnail_url
      const thumbnailUrl = newChapter.imagePreview || newChapter.thumbnail_url || '';

      // Always pass moduleId to ensure chapter is associated with correct module
      const result = await createChapter(moduleId, {
        title: newChapter.title,
        description: newChapter.description,
        thumbnail_url: thumbnailUrl,
        order: parseInt(newChapter.order) || 1,
        early_access_days: earlyAccessDays,
      });

      // console.log(`[Chapters] Chapter created successfully for moduleId: ${moduleId}`, result);

      if (newChapter.imagePreview && newChapter.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(newChapter.imagePreview);
      }

      setShowAddModal(false);
      // Optimistic: bump module's chapter count so Module page updates instantly on back navigation.
      bumpModuleChaptersCount(moduleId, 1);
      setAddChapterErrors({
        title: '',
        description: '',
        order: '',
        early_access_days: '',
      });
      setNewChapter({
        title: '',
        description: '',
        thumbnail_url: '',
        order: 1,
        early_access_days: '0',
        image: null,
        imagePreview: null,
      });
      // Reset guard flags to allow refresh
      lastModuleIdRef.current = null;
      isLoadingRef.current = false;
      setIsLoading(true);
      await loadData();
    } catch (err) {
      // console.error('Error adding chapter:', err);
      alert(err.message || 'Failed to add chapter. Please try again.');
    } finally {
      setAdding(false);
      setIsLoading(false);
    }
  };

  const cancelAddChapter = () => {
    if (newChapter.imagePreview && newChapter.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(newChapter.imagePreview);
    }
    setShowAddModal(false);
    setAddChapterErrors({
      title: '',
      description: '',
      order: '',
      early_access_days: '',
    });
    setNewChapter({
      title: '',
      description: '',
      thumbnail_url: '',
      order: 1,
      early_access_days: '0',
      image: null,
      imagePreview: null,
    });
  };

  const handleEdit = (chapterId) => {
    const chapter = chapters.find((c) =>
      c.id === chapterId || c.chapter_id === chapterId || c._id === chapterId
    );
    if (chapter) {
      const chId = chapter.chapter_id || chapter.id || chapter._id || chapterId;
      const thumbnailUrl = (chapter.thumbnail_url && String(chapter.thumbnail_url).trim()) ||
        (chapter.image && String(chapter.image).trim()) ||
        '';

      setEditingChapter({
        chapter_id: chId,
        title: chapter.title || chapter.name,
        description: chapter.description || '',
        thumbnail_url: thumbnailUrl,
        order: chapter.order || 1,
        early_access_days: getChapterEarlyAccessDays(chapter),
        is_active: chapter.is_active !== undefined ? chapter.is_active : true,
        image: null,
        imagePreview: thumbnailUrl || null,
      });
      setShowEditModal(true);
    }
  };

  const handleEditImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setEditingUploadError('');
      setEditingUploadingImage(true);
      setEditingChapter({
        ...editingChapter,
        image: file,
        imagePreview: previewUrl,
        thumbnail_url: '',
      });

      try {
        const uploadedUrl = await uploadImage(file);
        setEditingChapter(prev => ({ ...prev, imagePreview: uploadedUrl, thumbnail_url: uploadedUrl }));
        setEditingUploadError('');
      } catch (error) {
        console.error('Image upload failed:', error);
        setEditingUploadError(error.message || 'Failed to upload image. Please try again.');
        setEditingChapter(prev => ({ ...prev, image: null, imagePreview: null, thumbnail_url: '' }));
      } finally {
        setEditingUploadingImage(false);
      }
    }
  };

  const handleEditThumbnailUrlChange = (e) => {
    const url = e.target.value;
    if (editingChapter.imagePreview && editingChapter.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editingChapter.imagePreview);
    }
    setEditingChapter({
      ...editingChapter,
      thumbnail_url: url,
      image: null,
      imagePreview: url || null,
    });
  };

  const handleUpdateChapterSubmit = async () => {
    // Validate moduleId is present
    if (!moduleId) {
      alert('Module ID is missing. Cannot update chapter. Please navigate back to modules and try again.');
      return;
    }

    if (!editingChapter.title.trim() || !editingChapter.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const earlyAccessDays = parseEarlyAccessDays(editingChapter.early_access_days);
    if (Number.isNaN(earlyAccessDays) || earlyAccessDays < 0) {
      alert('Early access days must be an integer greater than or equal to 0');
      return;
    }

    const chapterId = editingChapter.chapter_id;
    if (!chapterId) {
      alert('Chapter ID is missing. Please try again.');
      return;
    }

    // Validate that the chapter belongs to the current module
    const chapter = chapters.find((c) =>
      String(c.id || c.chapter_id || c._id) === String(chapterId)
    );

    if (chapter) {
      const chapterModuleId = chapter.module_id || chapter.moduleId || chapter.module?.module_id || chapter.module?.id;
      if (chapterModuleId && String(chapterModuleId) !== String(moduleId)) {
        alert('This chapter does not belong to the current module. Please refresh the page.');
        return;
      }
    }

    setEditing(true);
    try {
      // Use uploaded URL from imagePreview or thumbnail_url
      const thumbnailUrl = editingChapter.imagePreview || editingChapter.thumbnail_url || '';

      // Update chapter (moduleId is validated but not sent in request body as API doesn't accept it)
      await updateChapter(moduleId, {
        chapter_id: String(chapterId),
        title: editingChapter.title,
        description: editingChapter.description,
        thumbnail_url: thumbnailUrl,
        order: parseInt(editingChapter.order) || 1,
        early_access_days: earlyAccessDays,
        is_active: editingChapter.is_active,
      });

      if (editingChapter.imagePreview && editingChapter.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(editingChapter.imagePreview);
      }

      setShowEditModal(false);
      setEditingChapter({
        chapter_id: null,
        title: '',
        description: '',
        thumbnail_url: '',
        order: 1,
        early_access_days: '0',
        is_active: true,
        image: null,
        imagePreview: null,
      });
      // Reset guard flags to allow refresh
      lastModuleIdRef.current = null;
      isLoadingRef.current = false;
      setIsLoading(true);
      await loadData();
    } catch (err) {
      // console.error('Error updating chapter:', err);
      alert(err.message || 'Failed to update chapter. Please try again.');
    } finally {
      setEditing(false);
      setIsLoading(false);
    }
  };

  const cancelEditChapter = () => {
    if (editingChapter.imagePreview && editingChapter.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editingChapter.imagePreview);
    }
    setShowEditModal(false);
    setEditingChapter({
      chapter_id: null,
      title: '',
      description: '',
      thumbnail_url: '',
      order: 1,
      early_access_days: '0',
      is_active: true,
      image: null,
      imagePreview: null,
    });
  };

  const handleDelete = (chapterId) => {
    const chapter = chapters.find((c) =>
      c.id === chapterId || c.chapter_id === chapterId || c._id === chapterId
    );
    setChapterToDelete(chapter);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    // Validate moduleId is present
    if (!moduleId) {
      alert('Module ID is missing. Cannot delete chapter. Please navigate back to modules and try again.');
      setShowDeleteModal(false);
      setChapterToDelete(null);
      return;
    }

    if (!chapterToDelete) {
      setShowDeleteModal(false);
      return;
    }

    const chapterId = chapterToDelete.chapter_id || chapterToDelete.id || chapterToDelete._id;

    if (!chapterId) {
      alert('Chapter ID is missing. Please try again.');
      return;
    }

    setDeleting(true);
    try {
      // Always pass moduleId to ensure chapter deletion is scoped to correct module
      await deleteChapter(moduleId, String(chapterId));
      setShowDeleteModal(false);
      // Optimistic: decrement module's chapter count so Module page updates instantly on back navigation.
      bumpModuleChaptersCount(moduleId, -1);
      setChapterToDelete(null);
      // Reset guard flags to allow refresh
      lastModuleIdRef.current = null;
      isLoadingRef.current = false;
      setIsLoading(true);
      await loadData();
    } catch (err) {
      // console.error('Error deleting chapter:', err);
      alert(err.message || 'Failed to delete chapter. Please try again.');
    } finally {
      setDeleting(false);
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setChapterToDelete(null);
  };

  const handleStatusChange = async (chapterId, newStatus) => {
    // Validate moduleId is present
    if (!moduleId) {
      alert('Module ID is missing. Cannot update chapter status. Please navigate back to modules and try again.');
      setOpenDropdownId(null);
      return;
    }

    try {
      const chapterIdStr = String(chapterId);
      const chapter = chapters.find((c) =>
        String(c.id || c.chapter_id || c._id) === chapterIdStr
      );

      if (!chapter) {
        alert('Chapter not found. Please refresh the page.');
        return;
      }

      const thumbnailUrl = (chapter.thumbnail_url && String(chapter.thumbnail_url).trim()) ||
        (chapter.image && String(chapter.image).trim()) ||
        '';

      // Always pass moduleId to ensure chapter update is scoped to correct module
      await updateChapter(moduleId, {
        chapter_id: chapterIdStr,
        title: chapter.title || chapter.name,
        description: chapter.description || '',
        thumbnail_url: thumbnailUrl,
        order: chapter.order || 1,
        early_access_days: getChapterEarlyAccessDays(chapter),
        is_active: newStatus,
      });
      // Close dropdown after selection
      setOpenDropdownId(null);
      // Reset guard flags to allow refresh
      lastModuleIdRef.current = null;
      isLoadingRef.current = false;
      setIsLoading(true);
      await loadData();
    } catch (err) {
      // console.error('Error updating chapter status:', err);
      alert(err.message || 'Failed to update chapter status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = (chapterId, chapterTitle) => {
    if (!chapterId || !moduleId) return;
    navigate(`/module/${moduleId}/chapters/${chapterId}/lessons`, {
      state: {
        fromName: chapterTitle,
        moduleName,
        chapterName: chapterTitle,
        unitName: null,
      },
    });
  };

  const handleSaveChapterOrder = async () => {
    // Validate moduleId is present
    if (!moduleId) {
      Toast.error('Module ID is missing. Cannot save chapter order.');
      return;
    }

    setReorderSaveLoading(true);
    try {
      const payload = reorderChapters.map((c, index) => ({
        chapter_id: String(c.id || c.chapter_id || c._id),
        order: index + 1,
      }));

      // Update each chapter's order via API
      const updatePromises = payload.map(async (item) => {
        const chapter = reorderChapters.find(
          (c) => String(c.id || c.chapter_id || c._id) === item.chapter_id
        );
        if (chapter) {
          const thumbnailUrl = (chapter.thumbnail_url && String(chapter.thumbnail_url).trim()) ||
            (chapter.image && String(chapter.image).trim()) ||
            '';

          return updateChapter(moduleId, {
            chapter_id: item.chapter_id,
            title: chapter.title || chapter.name,
            description: chapter.description || '',
            thumbnail_url: thumbnailUrl,
            order: item.order,
            early_access_days: getChapterEarlyAccessDays(chapter),
            is_active: chapter.is_active !== undefined ? chapter.is_active : true,
          });
        }
      });

      await Promise.all(updatePromises);
      setShowReorderModal(false);
      Toast.success('Chapter order updated successfully');
      // Reset guard flags to allow refresh
      lastModuleIdRef.current = null;
      isLoadingRef.current = false;
      setIsLoading(true);
      await loadData();
    } catch (err) {
      console.error(err);
      Toast.error('Failed to save chapter order');
    } finally {
      setReorderSaveLoading(false);
      setIsLoading(false);
    }
  };

  const handleUploadCSV = async () => {
    if (!selectedFile) {
      Toast.error('Please select a JSON file');
      return;
    }
    if (!moduleId) {
      Toast.error('Module ID is missing.');
      return;
    }
    setUploadingCsv(true);
    try {
      const res = await uploadQuizCsv(selectedFile, {
        module_id: moduleId,
      });
      const resultData = res?.data || {};
      setUploadResult({
        successful: resultData.successful || [],
        skipped: resultData.skipped || [],
        error: resultData.error || [],
      });
      setShowUploadModal(false);
      setSelectedFile(null);
      setShowUploadResultModal(true);
      lastModuleIdRef.current = null;
      invalidateLessonCache();
      invalidateLessonContentCache();
      setIsLoading(true);
      await loadData();
    } catch (err) {
      Toast.error(err.message || 'Failed to upload JSON');
    } finally {
      setUploadingCsv(false);
      setIsLoading(false);
    }
  };

  // Custom Grid Icon Component (3 rows, 2 dots each - vertical layout)
  const GridIcon = ({ size = 18 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400"
    >
      {/* Left column - 3 dots */}
      <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
      {/* Right column - 3 dots */}
      <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
    </svg>
  );

  const SortableChapterItem = ({ chapter, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: chapter.id || chapter.chapter_id || chapter._id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
            {index + 1}
          </div>

          <p className="text-black font-medium  flex-1  min-w-0 break-words line-clamp-2">
            {chapter.title || chapter.name}
          </p>



        </div>

        <div className="flex-shrink-0 ml-2">
          <GridIcon size={18} />
        </div>
      </div>
    );
  };


  // Memoized filtered chapters - double safety to ensure only chapters for current module are shown
  const filteredChapters = useMemo(() => {
    if (!moduleId || !chapters.length) {
      return [];
    }

    // Filter chapters to ensure they belong to the current module
    return chapters.filter((chapter) => {
      const chapterModuleId = chapter.module_id || chapter.moduleId || chapter.module?.module_id || chapter.module?.id;
      // Convert both to strings for comparison to handle number/string mismatches
      const matches = String(chapterModuleId) === String(moduleId);

      if (!matches && chapterModuleId) {
        // console.warn(`[Chapters] Chapter ${chapter.chapter_id || chapter.id} belongs to module ${chapterModuleId}, but current module is ${moduleId}`);
      }

      return matches;
    });
  }, [chapters, moduleId]);

  // Pagination range calculation
  const getPaginationRange = () => {
    const totalPages = Math.ceil(filteredChapters.length / limit);
    if (totalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(page - 1, 1);
    const rightSiblingIndex = Math.min(page + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let pageNum = startPage; pageNum <= endPage; pageNum += 1) {
      pages.push(pageNum);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  // Calculate paginated chapters
  const totalPages = Math.ceil(filteredChapters.length / limit);
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedChapters = filteredChapters.slice(startIndex, endIndex);

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="flex-1 bg-[#FDF6EF] flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
          {/* Header */}
          <div className="-mb-2 md:-mb-2 relative z-50">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <button
                onClick={() =>
                  navigate('/module', {
                    state: {
                      fromName: moduleName,
                      moduleName,
                    },
                  })
                }
                className="sm:p-2 rounded-lg hover:bg-white/50 transition-colors relative z-50"
              >
                <HiArrowLeft className="w-6 h-6 text-gray-800" />
              </button>
              <div className="flex flex-col md:flex-row md:items-baseline">
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                  Chapters
                </h1>
                {fromName && (
                  <span className="text-sm md:text-lg font-semibold text-gray-600 mt-0.5 md:mt-0 md:ml-2">
                    {fromName}
                  </span>
                )}
              </div>
            </div>

          </div>




          {/* Filters Bar */}
          <div className="mb-1 md:mb-0.5 mt-8 md:mt-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
              {/* Search Input - Full width on mobile */}
              <div className="w-full md:w-40">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] focus:outline-none focus:border-gray-300 bg-white"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>

              {/* Action Buttons - Second line on mobile, same line on desktop */}
              {moduleId && (
                <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                  >
                    <HiUpload className="w-4 h-4" />
                    <span>Upload File</span>
                  </button>
                  <button
                    onClick={() => {
                      if (!filteredChapters.length) return;
                      setReorderChapters(filteredChapters);
                      setShowReorderModal(true);
                    }}
                    disabled={!filteredChapters.length}
                    className={`flex items-center w-26 gap-2 px-3 py-2 text-sm font-semibold bg-brand text-white hover:shadow-lg rounded-xl shadow-md whitespace-nowrap flex-shrink-0 transition-all
                  
                    }`}
                  >
                    Edit Order
                  </button>
                  <button
                    onClick={handleAddChapter}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap"
                  >
                    <span className="text-base">+</span>
                    <span>Add Chapter</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Chapter List */}
          <div>
            {isLoading || paginationLoading ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
                <div className="flex items-center gap-3" role="status" aria-live="polite">
                  <svg
                    className="w-12 h-12 animate-spin"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#E9D5FF" />
                        <stop offset="25%" stopColor="#C084FC" />
                        <stop offset="50%" stopColor="#A855F7" />
                        <stop offset="75%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#6B21A8" />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="url(#purple-gradient)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="60 200"
                      fill="none"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                </div>
              </div>
            ) : filteredChapters.length === 0 ? (
              // Empty State
              <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
                <div className="max-w-md mx-auto text-center pointer-events-auto">
                  {String(debouncedSearch || '').trim().length > 0 ? (
                    <>
                      <p className="text-sm text-gray-500 mb-6">No chapters found. Try a different keyword.</p>
                    </>
                  ) : (
                    <>
                      <div className="mb-6">
                        <svg
                          className="w-24 h-24 mx-auto text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        No chapters added yet
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        Start by adding the first chapter for this module
                      </p>
                      <button
                        onClick={handleAddChapter}
                        className="inline-flex items-center  gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90"
                      >
                        <span className="text-base">+</span>
                        <span>Add Chapter</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 mt-4 gap-4">
                {paginatedChapters.map((chapter, index) => {
                  const isActive = chapter.is_active !== undefined ? chapter.is_active : true;
                  const chapterId = chapter.id || chapter.chapter_id || chapter._id;
                  const showDropdown = openDropdownId === chapterId;
                  const childCount = getChapterChildCount(chapter);
                  const earlyAccessDays = getChapterDisplayEarlyAccessDays(chapter);

                  return (
                    <div
                      key={chapterId}
                      onClick={() => handleCardClick(chapterId, chapter.title || chapter.name)}
                      className="bg-white rounded-xl p-4 sm:p-5 transition-shadow relative overflow-hidden hover:shadow-lg cursor-pointer"
                      style={{ boxShadow: '0px 3px 8px 0px #93939340' }}
                    >
                      <div className="absolute inset-0 z-0 -ml-5 opacity-30  pointer-events-none">
                        <img
                          src={require('../../assets/unitcard.png')}
                          alt=""
                          className="w-full h-full object-fill "
                        />
                      </div>


                      <div className='relative z-10'>
                        {/* Row 1: Title */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Order Number */}
                            {/* <span className="text-sm sm:text-base font-bold text-gray-600 flex-shrink-0">
                            {chapter.order || index + 1}.
                          </span> */}
                            {/* Chapter Title */}
                            <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate flex-1">
                              {chapter.title || chapter.name}
                            </h3>
                          </div>
                          <HiArrowLeft className={`w-4 h-4 flex-shrink-0 rotate-180 text-gray-400 mr-2`} />
                        </div>

                        {/* Row 2: Description */}
                        <div className="mb-6 h-[2em]">
                          <p className="text-xs sm:text-sm text-gray-500 line-clamp-2  overflow-hidden"
                          >
                            {chapter.description || 'No description available'}
                          </p>


                        </div>

                        {/* Row 3: Early access chip */}
                        <div className="mb-8 min-h-[24px] flex justify-start">
                          {earlyAccessDays > 0 && (
                            <div className="px-2 py-1 text-xs font-semibold rounded-lg bg-[#EFEBF3] text-brand whitespace-nowrap">
                              {`${earlyAccessDays} Days Early Access`}
                            </div>
                          )}
                        </div>


                        {/* Bottom-left: Child count */}
                        <div className="absolute -bottom-11 left-0.5 flex items-center">
                          <div className="px-2 py-2 text-xs font-semibold rounded-lg bg-gray-100 text-gray-700">
                            Units: <span className="text-gray-900">{childCount ?? 0}</span>
                          </div>
                        </div>

                        {/* Edit, Status and Delete Actions at Bottom Right */}
                        <div className="absolute -bottom-11 right-0.5 flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(chapterId);
                            }}
                            className="w-8 h-8 text-sm font-bold rounded-lg transition-colors hover:opacity-90 text-brand bg-[#EFEBF3] flex items-center justify-center"
                            title="Edit Chapter"
                          >
                            <HiPencil className="w-4 h-4" />
                          </button>
                          {/* Status Dropdown */}

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(chapterId);
                            }}
                            className="w-8 h-8 font-bold text-sm rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center"
                            title="Delete Chapter"
                          >
                            <HiTrash className="w-4 h-4" />
                          </button>
                          <div className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(showDropdown ? null : chapterId);
                              }}
                              className={`px-2 py-2 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 min-w-[80px] justify-center ${isActive
                                ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                                : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                                }`}
                            >
                              <span>{isActive ? 'Active' : 'Inactive'}</span>
                              <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-1 ${showDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showDropdown && (
                              <div className="absolute z-50 mt-1 right-0 w-auto min-w-[83px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 bottom-full mb-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(chapterId, !isActive);
                                  }}
                                  className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                                >
                                  <span>{isActive ? 'Inactive' : 'Active'}</span>
                                </button>
                              </div>
                            )
                            }
                          </div>
                        </div>

                        {/* Round Image at Bottom Right */}

                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {((totalPages > 1) || (totalPages === 1 && filteredChapters.length === 16)) && !isLoading && !paginationLoading && (
              <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((pageNum, idx) =>
                      pageNum === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${pageNum === page
                            ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                            : 'text-gray-900 hover:bg-gray-100'
                            }`}
                          aria-current={pageNum === page ? 'page' : undefined}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Chapter Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add New Chapter</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowAddModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Chapter Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newChapter.title}
                  onChange={(e) => {
                    setNewChapter({ ...newChapter, title: e.target.value });
                    if (addChapterErrors.title) {
                      setAddChapterErrors({ ...addChapterErrors, title: '' });
                    }
                  }}
                  placeholder="Enter chapter title"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${addChapterErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {addChapterErrors.title && (
                  <p className="mt-1 text-xs text-red-500">{addChapterErrors.title}</p>
                )}
              </div>
              <div>
                <label className="-mt-1 block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newChapter.description}
                  onChange={(e) => {
                    setNewChapter({ ...newChapter, description: e.target.value });
                    if (addChapterErrors.description) {
                      setAddChapterErrors({ ...addChapterErrors, description: '' });
                    }
                  }}
                  placeholder="Enter chapter description"
                  rows="2"
                  className={`w-full px-3 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2resize-none ${addChapterErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {addChapterErrors.description && (
                  <p className="mt-1 text-xs text-red-500">{addChapterErrors.description}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Early Access Days
                </label>
                <input
                  type="number"
                  value={newChapter.early_access_days}
                  onChange={(e) => {
                    setNewChapter({ ...newChapter, early_access_days: e.target.value });
                    if (addChapterErrors.early_access_days) {
                      setAddChapterErrors({ ...addChapterErrors, early_access_days: '' });
                    }
                  }}
                  placeholder="0"
                  min="0"
                  step="1"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${addChapterErrors.early_access_days ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {addChapterErrors.early_access_days && (
                  <p className="mt-1 text-xs text-red-500">{addChapterErrors.early_access_days}</p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={cancelAddChapter}
                disabled={adding}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAddChapterSubmit}
                disabled={adding}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  'Add Chapter'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Chapter Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Edit Chapter</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowEditModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Chapter Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingChapter.title}
                  onChange={(e) => setEditingChapter({ ...editingChapter, title: e.target.value })}
                  placeholder="Enter chapter title"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none "
                />
              </div>
              <div>
                <label className="-mt-1 block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editingChapter.description}
                  onChange={(e) => setEditingChapter({ ...editingChapter, description: e.target.value })}
                  placeholder="Enter chapter description"
                  rows="2"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none  resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Early Access Days
                </label>
                <input
                  type="number"
                  value={editingChapter.early_access_days}
                  onChange={(e) => setEditingChapter({ ...editingChapter, early_access_days: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                />
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingChapter.is_active}
                    onChange={(e) => setEditingChapter({ ...editingChapter, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-xs font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={cancelEditChapter}
                disabled={editing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateChapterSubmit}
                disabled={editing}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {editing ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Chapter'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && chapterToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800">Delete Chapter</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowDeleteModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete the chapter "{chapterToDelete.title || chapterToDelete.name}"?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload JSON Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Upload Content via JSON</h2>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload a JSON file containing your learning content. Ensure the file follows the required format for successful processing.
              </p>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-700">Select JSON File</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand/10 file:text-brand hover:file:bg-brand/20 cursor-pointer"
                />
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
                disabled={uploadingCsv}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadCSV}
                disabled={uploadingCsv || !selectedFile}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploadingCsv ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Result Modal */}
      {showUploadResultModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">JSON Upload Summary</h2>
              <button onClick={() => setShowUploadResultModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenSection(openSection === 'success' ? null : 'success')} className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 transition">
                  <span className="font-semibold text-green-700">Successfully Added ({uploadResult.successful.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-green-700 ${openSection === 'success' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openSection === 'success' && uploadResult.successful.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-green-700 space-y-2">
                    {uploadResult.successful.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Row {item.row}:</span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Chapter: {item.chapter}- {item.chapter_name}</div>
                          <div>Unit: {item.lesson}- {item.lesson_name}</div>
                          <div>Lesson: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.question_text}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenSection(openSection === 'skipped' ? null : 'skipped')} className="w-full flex items-center justify-between px-4 py-3 bg-yellow-50 hover:bg-yellow-100 transition">
                  <span className="font-semibold text-yellow-700">Skipped Records ({uploadResult.skipped.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-yellow-700 ${openSection === 'skipped' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openSection === 'skipped' && uploadResult.skipped.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-yellow-700 space-y-2">
                    {uploadResult.skipped.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Row {item.row}:</span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Chapter: {item.chapter}- {item.chapter_name}</div>
                          <div>Lesson: {item.lesson}- {item.lesson_name}</div>
                          <div>Content: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.reason}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {uploadResult.error.length > 0 && (
                <div className="border border-red-200 rounded-xl overflow-hidden bg-red-50">
                  <button onClick={() => setOpenSection(openSection === 'error' ? null : 'error')} className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-100 transition">
                    <span className="font-semibold text-red-700">Errors ({uploadResult.error.length})</span>
                    <HiChevronDown className={`w-5 h-5 text-red-700 ${openSection === 'error' ? 'rotate-180' : ''}`} />
                  </button>
                  <ul className={`px-4 text-sm text-red-700 space-y-2 overflow-hidden list-disc list-inside transition-all overflow-y-auto ${openSection === 'error' ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                    {uploadResult.error.map((item, i) => (
                      <li key={i}>Row {item.row} — {item.error || item.reason || item.message || 'Unknown server error'}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => { setShowUploadResultModal(false); setOpenSection(null); }} className="px-5 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden overflow-x-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-black/60 text-lg font-semibold">
                Reorder Chapters
              </h2>
              {/* <button
                onClick={() => setShowReorderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button> */}
            </div>

            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop chapters to reorder them. Click <b>Save Order</b> to apply.
            </p>

            {/* Drag List */}
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={() => {
                // Prevent horizontal scrolling during drag
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                // Restore scrolling after drag
                document.body.style.overflowX = '';

                const { active, over } = event;
                if (!over || active.id === over.id) return;

                setReorderChapters((prev) => {
                  const oldIndex = prev.findIndex(
                    (c) => (c.id || c.chapter_id || c._id) === active.id
                  );
                  const newIndex = prev.findIndex(
                    (c) => (c.id || c.chapter_id || c._id) === over.id
                  );
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                // Restore scrolling if drag is cancelled
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderChapters.map(
                  (c) => c.id || c.chapter_id || c._id
                )}
                strategy={verticalListSortingStrategy}
              >
                {/* Calculate height: each item is ~72px (p-4 = 16px top/bottom + content ~40px) + gap-3 (12px) = ~84px per item, 5 items = ~420px */}
                <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0" style={{ maxHeight: '420px' }}>
                  {reorderChapters.map((chapter, index) => (
                    <SortableChapterItem
                      key={chapter.id || chapter.chapter_id || chapter._id}
                      chapter={chapter}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowReorderModal(false)}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveChapterOrder}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaveLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chapters;

